import { __decorate } from "tslib";
import { getCurrentFieldSettingApi, getFieldSettingApi, updateSpaceFieldSettingApi, updateTemplateSettingApi, getSpaceFieldTemplateListApi, saveSpaceFieldTemplateApi, deleteFieldTemplateApi, postCheckFieldTemplateApi } from '@cores/apis/workspace_api/workspace';
import _ from 'lodash';
import { Toast } from 'patsnap-biz';
import { injectService, provide } from '@/inversify.config';
import { i18nSymbol } from '@/i18n';
export const FieldDisplayTemplateSettingsServiceSymbol = Symbol.for('FieldDisplayTemplateSettingsService');
export var ViewType;
(function (ViewType) {
    ViewType["TABLE"] = "tablelist";
    ViewType["IMAGE_TEXT"] = "standardlist";
    ViewType["FULL_IMAGE"] = "pdfimage";
    ViewType["FAST_TABLE"] = "fasttablelist";
})(ViewType || (ViewType = {}));
export const MAX_STANDARD_FIELD_COUNT = 16; // 全文附图 专利字段最多16个
export const VIEW_TITLE_KEYS = {
    tablelist: 'spaceFieldTemplate.tableView',
    standardlist: 'spaceFieldTemplate.imageView',
    pdfimage: 'spaceFieldTemplate.fullimgView',
    thumbnail: 'spaceFieldTemplate.thumbView',
    filterItems: 'spaceFieldTemplate.filterView',
};
export const TAB_TO_TRANSLATION = {
    PATENT: 'commonPatent',
    PAPER: 'commonPaper',
    MR: 'commonMarket',
    VC: 'commonInvestment',
    MA: 'commonMerger',
};
export const VIEW_TO_TRANSLATION = {
    tablelist: 'BASIC',
    standardlist: 'BASIC',
    pdfimage: 'BASIC',
    thumbnail: 'BASIC',
    filterItems: 'filterFields',
};
export class FieldDisplayTemplateSettingsService {
    constructor(spaceId) {
        this.spaceId = spaceId;
        this.currentTab = 'PATENT';
        this.isLoading = false;
        this.isInitd = false;
        this.fieldStructure = {};
        this.fieldSettings = {};
        this.templates = [{
                label: '',
                value: 'custom',
            }];
        this.currentTemplate = 'custom';
        this.lastTemplate = 'custom';
        this.isModified = false;
        this.fieldManagerDialog = {
            isVisible: false,
            dataType: undefined,
            viewType: undefined,
            visibleFields: [],
            close() {
                this.isVisible = false;
                this.dataType = undefined;
                this.viewType = undefined;
            },
            open(dataType, viewType, visibleFields) {
                this.isVisible = true;
                this.dataType = dataType;
                this.viewType = viewType;
                this.visibleFields = visibleFields;
            },
        };
        this.applyManagerDialog = {
            isVisible: false,
            error: undefined,
            close() {
                this.isVisible = false;
            },
            open() {
                this.isVisible = true;
                this.error = undefined;
            },
        };
        this.saveAsNewTemplateDialog = {
            isVisible: false,
            error: undefined,
            close() {
                this.isVisible = false;
            },
            open() {
                this.isVisible = true;
                this.error = undefined;
            },
        };
        this.checkTemplateCustomFieldDialog = {
            isVisible: false,
            error: undefined,
            fieldList: [],
            close() {
                this.isVisible = false;
            },
            open(fieldList) {
                this.fieldList = fieldList;
                this.isVisible = true;
                this.error = undefined;
            },
        };
        if (!spaceId)
            throw new Error('spaceId is required');
        this.spaceId = spaceId;
    }
    get isCustomTemplate() {
        return this.currentTemplate === 'custom';
    }
    get currentTemplateName() {
        const i18n = injectService(i18nSymbol);
        return this.templates.find(t => t.value === this.currentTemplate)?.label || i18n.t('spaceFieldTemplate.selectTemplate');
    }
    // 设置对应类型的字段设置
    setFieldSettings(tab, view, setting) {
        this.isModified = true;
        this.fieldSettings[tab][view] = setting;
        // 如果是来自过滤，不需要同步自定义字段
        if (view === 'filterItems') {
            return;
        }
        // 找出自定义字段，并且应用给其他视图
        const customFields = [];
        for (const field of setting) {
            if (this.isCustomField(field)) {
                customFields.push(field);
            }
        }
        // 更新每一个设置的自定义字段
        for (const t in this.fieldSettings) {
            for (const v in this.fieldSettings[t]) {
                if ((t !== tab || v !== view) && v !== 'filterItems') {
                    const removeUnusedFields = this.fieldSettings[t][v].map((f) => {
                        if (this.isCustomField(f) && customFields.indexOf(f) === -1) {
                            return '';
                        }
                        return f;
                    });
                    this.fieldSettings[t][v] = _.compact(_.union(removeUnusedFields, customFields));
                }
            }
        }
    }
    // 初始化字段设置
    initFieldSetting() {
        this.isLoading = true;
        Promise.all([this.fetchTemplateList(), this.fetchCurrentFieldSetting()]).then(([templates, fields]) => {
            this.isInitd = true;
            this.templates = templates.map(t => {
                return {
                    label: t.field_template_name,
                    value: t.field_template_id,
                };
            });
            this.fieldStructure = fields.fields_structure;
            this.fieldSettings = fields.field_setting;
        }).then(() => {
            this.currentTemplate = 'custom';
            this.isModified = false;
        }).finally(() => {
            this.isLoading = false;
        });
    }
    // 获取当前的字段设置
    fetchCurrentFieldSetting({ field_template_id = '' } = {}) {
        return getCurrentFieldSettingApi({
            space_id: this.spaceId,
            field_template_id: this.isCustomTemplate ? '' : field_template_id,
        }).catch((error) => {
            if (error._error_msg) {
                Toast({
                    type: 'error',
                    message: error._error_msg,
                    showClose: true,
                });
            }
            throw error;
        });
    }
    // 获取单个类型字段设置
    fetchFieldSetting(data_type, view_type) {
        return getFieldSettingApi({
            space_id: this.spaceId,
            data_type,
            view_type,
        }).catch((error) => {
            if (error._error_msg) {
                Toast({
                    type: 'error',
                    message: error._error_msg,
                    showClose: true,
                });
            }
            throw error;
        });
    }
    // 获取模版列表
    fetchTemplateList() {
        return getSpaceFieldTemplateListApi({
            space_id: this.spaceId,
        }).catch((error) => {
            if (error._error_msg) {
                Toast({
                    type: 'error',
                    message: error._error_msg,
                    showClose: true,
                });
            }
            throw error;
        });
    }
    // 更新或者保存模版的字段设置
    updateOrSaveTemplateFieldSetting({ apply_to_all_folder = false } = {}) {
        // @ts-ignore todo
        return updateSpaceFieldSettingApi({
            space_id: this.spaceId,
            field_setting: this.fieldSettings,
            apply_to_all_folder,
        });
    }
    updateTemplateSetting() {
        return updateTemplateSettingApi({
            field_template_id: this.currentTemplate,
            field_setting: this.fieldSettings,
            space_id: this.spaceId,
        });
    }
    // 保存新模版
    saveAsNewTemplate({ field_template_name = '' } = {}) {
        return saveSpaceFieldTemplateApi({
            space_id: this.spaceId,
            field_template_name,
            field_setting: this.fieldSettings,
        }).then(res => {
            this.templates.push({
                label: res.field_template_name,
                value: res.field_template_id,
            });
            this.currentTemplate = res.field_template_id;
            this.isModified = false;
        }).catch((error) => {
            if (error._error_msg) {
                if (error._error_code === 70111605) {
                    Toast({
                        type: 'warning',
                        message: error._error_msg,
                        showClose: true,
                    });
                    this.saveAsNewTemplateDialog.close();
                }
                else {
                    this.saveAsNewTemplateDialog.error = new Error(error._error_msg);
                }
            }
            throw error;
        });
    }
    // 切换模版
    switchTemplate(field_template_id) {
        this.isLoading = true;
        this.lastTemplate = this.currentTemplate;
        this.currentTemplate = field_template_id;
        this.fetchCurrentFieldSetting({
            field_template_id,
        }).then((fields) => {
            this.fieldStructure = fields.fields_structure;
            this.fieldSettings = fields.field_setting;
            this.isModified = false;
        }).catch((error) => {
            if (error._error_msg) {
                Toast({
                    type: 'error',
                    message: error._error_msg,
                    showClose: true,
                });
            }
            this.currentTemplate = this.lastTemplate;
            throw error;
        }).finally(() => {
            this.isLoading = false;
        });
    }
    // 删除模版
    deleteTemplate(field_template_id) {
        this.isLoading = true;
        const target = field_template_id || this.currentTemplate;
        const targetName = this.currentTemplateName;
        return deleteFieldTemplateApi({
            space_id: this.spaceId,
            field_template_id: target,
        }).then(() => {
            this.templates = this.templates.filter(t => t.value !== target);
            this.isModified = false;
            this.switchTemplate('custom');
            return targetName;
        }).catch((error) => {
            if (error._error_msg) {
                Toast({
                    type: 'error',
                    message: error._error_msg,
                    showClose: true,
                });
            }
            throw error;
        }).finally(() => {
            this.isLoading = false;
        });
    }
    checkTemplateCustomField(tempId) {
        // @ts-ignore todo
        return postCheckFieldTemplateApi({
            space_id: this.spaceId,
            field_template_id: tempId,
        });
    }
    getFieldProps(field) {
        return _.get(this.fieldStructure, field, {});
    }
    getFieldProp(field, key, defaultValue) {
        return _.get(this.fieldStructure, `${field}.${key}`, defaultValue) || defaultValue;
    }
    isCustomField(field) {
        return this.getFieldProp(field, 'is_custom', false);
    }
    isSpaceScope(field) {
        return this.getFieldProp(field, 'field_label_scope') === 'SPACE';
    }
    fieldI18n(field, tab, view) {
        const i18n = injectService(i18nSymbol);
        const i18nNameSpace = `${TAB_TO_TRANSLATION[tab]}.${VIEW_TO_TRANSLATION[view]}`;
        if (this.getFieldProp(field, 'field_type') === 'OPTION_COLOUR_LAB') {
            return i18n.t(`${i18nNameSpace}.PATENT_COLOUR`);
        }
        if (this.isCustomField(field)) {
            return _.get(this.fieldStructure, `${field}.field_name`);
        }
        if (i18n.te(`${i18nNameSpace}.${field}`)) {
            return i18n.t(`${i18nNameSpace}.${field}`);
        }
        if (i18n.te(`advanceSort.extendFields.${field}`)) {
            return i18n.t(`advanceSort.extendFields.${field}`);
        }
        return field;
    }
}
export const FieldDisplayTemplateSettingsServiceFactorySymbol = Symbol.for('FieldDisplayTemplateSettingsServiceFactory');
let FieldDisplayTemplateSettingsServiceFactory = class FieldDisplayTemplateSettingsServiceFactory {
    getInstance(spaceId) {
        return new FieldDisplayTemplateSettingsService(spaceId);
    }
};
FieldDisplayTemplateSettingsServiceFactory = __decorate([
    provide(FieldDisplayTemplateSettingsServiceFactorySymbol)
], FieldDisplayTemplateSettingsServiceFactory);
export { FieldDisplayTemplateSettingsServiceFactory };
